<script setup lang="ts">
import { computed } from "vue";

import FilterBar from "@/components/gallery/FilterBar.vue";
import GalleryGrid from "@/components/gallery/GalleryGrid.vue";
import VoiceSection from "@/components/gallery/VoiceSection.vue";
import { DEMO_GUESTS } from "@/data/demo-media";
import { pluralize } from "@/lib/format";
import { useAlbumStore } from "@/stores/album";
import { FILTERS } from "@/types/album";

/**
 * Port de `.album-section` (design-reference/styles.css) : bandeau de
 * titre, statistiques, barre de filtres, grille, note de fin.
 */

withDefaults(defineProps<{ highlightedId?: string | null }>(), { highlightedId: null });
const emit = defineEmits<{ "open-media": [id: string] }>();

const album = useAlbumStore();

const guestTotal = computed(() => DEMO_GUESTS.initials.length + DEMO_GUESTS.extra);

const filterAnnouncement = computed(() => {
  const label = FILTERS.find((filter) => filter.id === album.filter)?.label ?? "Tous";
  return `${pluralize(album.filteredItems.length, "souvenir")} — ${label}`;
});
</script>

<template>
  <section class="album-section" data-testid="album-section" aria-labelledby="albumTitle">
    <div class="album-heading">
      <div>
        <span class="section-kicker">Souvenirs partagés</span>
        <h2 id="albumTitle">Leur journée,<br /><em>vue par vous.</em></h2>
      </div>
      <div class="album-stats">
        <div>
          <strong data-testid="media-count">{{ album.totalCount }}</strong>
          <span>souvenirs</span>
        </div>
        <div class="avatar-stack" :aria-label="`${guestTotal} invités ont participé`">
          <span v-for="initial in DEMO_GUESTS.initials" :key="initial">{{ initial }}</span>
          <span>+{{ DEMO_GUESTS.extra }}</span>
        </div>
      </div>
    </div>

    <FilterBar :model-value="album.filter" @update:model-value="album.setFilter" />

    <!-- Le filtrage est purement visuel : sans cette annonce, un lecteur
         d'écran ne sait ni que la grille a changé, ni ce qu'elle contient. -->
    <p class="u-sr-only" role="status" aria-live="polite" data-testid="gallery-announcement">
      {{ filterAnnouncement }}
    </p>

    <!-- Sur mobile, la mosaïque déroule sur plusieurs écrans : reléguée
         dessous, la section vocale n'était jamais atteinte. L'ordre visuel
         s'inverse donc en dessous de 760px, sans toucher à l'ordre du
         document — la mosaïque reste le contenu principal pour un lecteur
         d'écran comme pour le référencement. -->
    <div class="album-content">
      <GalleryGrid
        :items="album.filteredItems"
        :highlighted-id="highlightedId"
        @select="emit('open-media', $event)"
      />

      <!-- `VoiceSection` a plusieurs racines (la section et ses dialogues) :
           une classe posée dessus ne s'appliquerait à aucune. D'où ce
           conteneur, qui porte l'ordre visuel. -->
      <div class="album-content__voices">
        <VoiceSection />
      </div>
    </div>

    <p class="gallery-end">
      <span aria-hidden="true">✦</span>
      Vous êtes arrivé au début de leur histoire
    </p>
  </section>
</template>

<style scoped>
.album-content {
  display: flex;
  flex-direction: column;
}

@media (max-width: 759px) {
  .album-content__voices {
    order: -1;
  }
}

.album-section {
  position: relative;
  padding: 42px 14px 120px;
  background:
    linear-gradient(rgb(248 246 239 / 0.74), rgb(248 246 239 / 0.82)),
    url("/assets/watercolor-floral-paper.webp") top center / min(100%, 1000px) auto repeat-y;
}

.album-section::before {
  position: absolute;
  top: 210px;
  right: 6%;
  width: 110px;
  height: 1px;
  background: rgb(183 114 101 / 0.25);
  box-shadow: 0 5px 0 rgb(183 114 101 / 0.1);
  content: "";
  transform: rotate(-7deg);
}

.album-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 20px;
  max-width: 1120px;
  margin: 0 auto 30px;
}

.section-kicker {
  color: var(--color-rose-strong);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.17em;
  text-transform: uppercase;
}

.album-heading h2 {
  margin: 9px 0 0;
  font-family: var(--font-serif);
  font-size: clamp(36px, 9vw, 72px);
  font-weight: 400;
  line-height: 0.92;
  letter-spacing: -0.035em;
  -webkit-text-stroke: 0.25px currentColor;
}

.album-heading h2 em {
  color: var(--color-rose);
  font-family: var(--font-script);
  font-size: 0.95em;
  font-style: normal;
}

.album-stats {
  flex: 0 0 auto;
  text-align: right;
}

.album-stats > div:first-child strong,
.album-stats > div:first-child span {
  display: block;
}

.album-stats > div:first-child strong {
  font-family: var(--font-serif);
  font-size: 28px;
  font-weight: 400;
  -webkit-text-stroke: 0.15px currentColor;
}

.album-stats > div:first-child span {
  color: var(--color-muted);
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.avatar-stack {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
  padding-right: 1px;
}

/* Initiales en `--color-ink` et non en blanc : sur ces pastilles pastel, du
   blanc ne dépassait pas 2,1:1. En encre, chaque combinaison tient 4,4:1 ou
   plus, sans toucher aux couleurs elles-mêmes. */
.avatar-stack span {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  margin-right: -7px;
  color: var(--color-ink);
  border: 2px solid var(--color-paper);
  border-radius: 50%;
  background: var(--color-sky);
  font-family: var(--font-serif);
  font-size: 12px;
}

.avatar-stack span:nth-child(2) {
  background: var(--color-rose-light);
}

.avatar-stack span:nth-child(3) {
  background: var(--color-mustard);
}

.avatar-stack span:last-child {
  color: var(--color-ink);
  background: var(--color-paper-2);
}

.gallery-end {
  margin: 50px 0 0;
  color: var(--color-muted);
  font-family: var(--font-serif);
  font-size: 14px;
  font-style: italic;
  text-align: center;
}

.gallery-end span {
  display: block;
  margin-bottom: 8px;
  color: var(--color-rose);
  font-size: 15px;
}

@media (min-width: 760px) {
  .album-section {
    padding-top: 80px;
    padding-right: 30px;
    padding-left: 30px;
  }
}

@media (min-width: 1100px) {
  .album-section {
    padding-top: 105px;
  }
}
</style>
